<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractEditorFieldset',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    contract: {
      type: Object,
      required: true,
    },
    target: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelAlign = ref('right');
  const ux = ref([]);

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'labelAlign' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { labelAlign, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.target,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('target');
    }
  );

  const act_7315092382398562_reloadComponentData_ihws0zs2u =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_fgkwbc07v =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ihws0zs2u,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_fgkwbc07v],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_fgkwbc07v,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_fgkwbc07v = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_fgkwbc07v.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <FieldsetContractGeneralData
      :contract="contract"
      :labelAlign="labelAlign"
      v-if="!target || target === 'general'"
    >
    </FieldsetContractGeneralData>
    <hr v-if="!target" />
    <FieldsetContractNumbersData
      :contract="contract"
      :labelAlign="labelAlign"
      v-if="!target || target === 'general'"
    >
    </FieldsetContractNumbersData>
    <hr v-if="!target" />
    <FieldsetContractCompanyData
      :contract="contract"
      :labelAlign="labelAlign"
      v-if="!target || target === 'company'"
    >
    </FieldsetContractCompanyData>
    <hr v-if="!target" />
    <FieldsetContractClientData
      :contract="contract"
      :labelAlign="labelAlign"
      v-if="!target || target === 'counterparty'"
    >
    </FieldsetContractClientData>
    <hr v-if="!target" />
    <FieldsetContractRentalData
      :contract="contract"
      :labelAlign="labelAlign"
      v-if="!target || target === 'rental'"
    >
    </FieldsetContractRentalData>
    <hr v-if="!target" />
    <FieldsetContractLifecycle
      :contract="contract"
      :labelAlign="labelAlign"
      v-if="!target || target === 'lifecycle'"
    >
    </FieldsetContractLifecycle>
    <hr v-if="!target" />
    <FieldsetContractInvoicing
      :contract="contract"
      :labelAlign="labelAlign"
      v-if="!target || target === 'invoicing'"
    >
    </FieldsetContractInvoicing>
  </ItpUsercontrol>
</template>
